import { MeetingAttemptResponseV2 } from '@chilipiper/api-client/src/user-availability'
import {
  getAdditionalRequiredAttendeesWithFallback,
  getUserExpectedHost,
  getRoundRobinAndOwnershipExpectedHost,
  getRequiredUsersFromGroupLinkEnhanced,
  BookerSettings,
  MeetingLimitSource,
} from '../helpers'
import { AvailabilityOptions } from '../types'
import {
  LinkResourceEnhanced,
  OwnershipLinkRoutingResult,
  RoundRobinLinkRoutingResult,
  isWithDistribution,
  getRoutingResultAvailabilityOptions,
  getFirstAssignmentFromRoutingResult,
} from '../../load-meeting-attempt'

export const useAvailabilityOptionsFromMeetingAttempt = ({
  linkResource,
  meetingAttempt,
  linkMeetingLimit,
  bookerSettings,
  routingResult,
}: {
  linkResource?: LinkResourceEnhanced
  meetingAttempt?: MeetingAttemptResponseV2
  linkMeetingLimit?: MeetingLimitSource
  bookerSettings?: BookerSettings
  routingResult?: OwnershipLinkRoutingResult | RoundRobinLinkRoutingResult
}): AvailabilityOptions | undefined => {
  if (meetingAttempt?.type !== 'Available') {
    return undefined
  }

  const productFeature = meetingAttempt?.attempt.productFeature

  const additionalRequiredAttendees = getAdditionalRequiredAttendeesWithFallback({
    meetingAttemptAttendees: meetingAttempt?.attempt.additionalAttendees,
    fallbackAttendees: linkResource?.requiredAttendees,
  })

  if (bookerSettings && bookerSettings.type !== 'NotInvited' && !bookerSettings.isOptional) {
    additionalRequiredAttendees.push(bookerSettings.id)
  }

  // Handle individual scheduling links
  if (
    productFeature?.type === 'AdminIndividualSchedulingLink' ||
    productFeature?.type === 'PersonalIndividualSchedulingLink'
  ) {
    return {
      userIds: [productFeature.hostId, ...additionalRequiredAttendees],
      expectedHost: getUserExpectedHost({
        bookerSettings,
        firstAssignment: { userId: productFeature.hostId },
      }),
      meetingLimit: linkMeetingLimit,
    }
  }

  // Handle HandoffRouting
  if (productFeature?.type === 'HandoffRouting' && linkResource?.type === 'HandoffRouter') {
    const selectedAssignees = linkResource.selectedAssignees

    if (!selectedAssignees) {
      return undefined
    }

    const distributionAssignments = selectedAssignees.filter(
      assignee => assignee.type === 'FromDistribution' || assignee.type === 'UserFromDistribution'
    )
    const userOrOwnershipAssignments = selectedAssignees.filter(
      assignee => assignee.type === 'User'
    )

    const userIds = userOrOwnershipAssignments.map(assignee => assignee.id)

    // Get first assignment for expected host calculation
    const firstAssignment = selectedAssignees[0]

    // If no distribution assignments, return user-based structure
    if (!distributionAssignments.length) {
      const firstUserId = userIds[0]

      return {
        userIds: [...userIds, ...additionalRequiredAttendees],
        expectedHost: getUserExpectedHost({
          bookerSettings,
          firstAssignment: { userId: firstUserId },
        }),
        meetingLimit: {
          type: 'NoMeetingLimit',
        },
      }
    }

    // Mixed or distribution-only scenario - use first assignment for expected host
    const expectedHost =
      firstAssignment.type === 'FromDistribution' || firstAssignment.type === 'UserFromDistribution'
        ? getRoundRobinAndOwnershipExpectedHost({
            bookerSettings,
            firstAssignment: { distributionId: firstAssignment.distributionId },
          })
        : getUserExpectedHost({
            bookerSettings,
            firstAssignment: { userId: firstAssignment.id },
          })

    return {
      distributionAttendees: distributionAssignments.map(assignee => ({
        userIds: assignee.type === 'FromDistribution' ? assignee.users : [assignee.user],
        distributionId: assignee.distributionId,
        required: true,
      })),
      expectedHost,
      additionalRequiredAttendees: additionalRequiredAttendees,
      ownershipAndUsersAttendees: userIds,
    }
  }

  // Handle RoundRobinSchedulingLink
  if (
    productFeature?.type === 'RoundRobinSchedulingLink' &&
    linkResource?.type === 'RoundRobinSchedulingLink' &&
    routingResult &&
    isWithDistribution(routingResult)
  ) {
    const firstAssignment = getFirstAssignmentFromRoutingResult(routingResult)
    const expectedHost = getRoundRobinAndOwnershipExpectedHost({
      bookerSettings,
      firstAssignment,
    })

    return getRoutingResultAvailabilityOptions({
      routingResult,
      expectedHost,
      additionalRequiredAttendees,
    })
  }

  // Handle OwnershipSchedulingLink
  if (
    productFeature?.type === 'OwnershipSchedulingLink' &&
    linkResource?.type === 'OwnershipSchedulingLink' &&
    routingResult
  ) {
    const firstAssignment = getFirstAssignmentFromRoutingResult(routingResult)

    const expectedHost = getRoundRobinAndOwnershipExpectedHost({
      bookerSettings,
      firstAssignment,
    })

    return getRoutingResultAvailabilityOptions({
      routingResult,
      expectedHost,
      additionalRequiredAttendees,
    })
  }

  // Handle GroupSchedulingLink
  if (
    productFeature?.type === 'GroupSchedulingLink' &&
    linkResource?.type === 'GroupSchedulingLink'
  ) {
    const groupRequiredAttendees = getRequiredUsersFromGroupLinkEnhanced(
      linkResource,
      meetingAttempt?.attempt
    )

    // Determine the expected host
    const hostId = linkResource.hostId

    return {
      userIds: groupRequiredAttendees,
      expectedHost: {
        userId: hostId,
        type: 'User',
      },
      meetingLimit: linkMeetingLimit,
    }
  }

  return undefined
}
